// Customer Profiles pages (mig 0017): list with filters, and a detail page
// with contact, OTP channels, the accounts that roll up to the profile and
// its change history.

import { Fragment, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import { Button } from '@/components/ui/button';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { accounts, customerProfiles, lookups } from '@/lib/api';
import { useMe } from '@/hooks/useMe';
import { formatAddress, platformLabel } from '@frt/shared-types';
import type { CustomerProfileRow, OobEntry, OobStatus } from '@frt/shared-types';
import { Dialog } from '@/components/ui/dialog';
import { HistoryCard } from '../assets/pages';
import { ProfileFormDialog, modeLabel } from './profile-form';

function useCanEdit(): boolean {
  const { data: me } = useMe();
  return me?.role === 'admin' || me?.role === 'senior';
}

type SortKey = 'customer' | 'platform' | 'tester' | 'phone';
type Expanded = { id: number; kind: 'tester' | 'phone' } | null;

const SORT_VALUE: Record<SortKey, (p: CustomerProfileRow) => string> = {
  customer: (p) => p.frt_customer_code,
  platform: (p) => platformLabel({ name: p.platform_name, type: p.platform_type_name }),
  tester: (p) => p.tester_display ?? 'Shared',
  phone: (p) => p.phone_display ?? '',
};

export function ProfilesListPage() {
  const canEdit = useCanEdit();
  const [q, setQ] = useState('');
  const [platformId, setPlatformId] = useState(0);
  const [active, setActive] = useState<'true' | 'false' | 'all'>('true');
  const [creating, setCreating] = useState(false);
  // Column filters + sort (client-side over the loaded list).
  const [customerFilter, setCustomerFilter] = useState('');
  const [platformFilter, setPlatformFilter] = useState('');
  const [testerFilter, setTesterFilter] = useState('');
  const [phoneFilter, setPhoneFilter] = useState('');
  const [sort, setSort] = useState<{ key: SortKey; dir: 1 | -1 }>({ key: 'customer', dir: 1 });
  const [expanded, setExpanded] = useState<Expanded>(null);

  const { data: platforms } = useQuery({
    queryKey: ['lookups', 'platform-options'],
    queryFn: () => lookups.platformOptions.list(),
  });
  const { data, isLoading, error } = useQuery({
    queryKey: ['customer-profiles', { q, platformId, active }],
    queryFn: () =>
      customerProfiles.list({ q: q || undefined, platform_id: platformId || undefined, active }),
  });
  const all = data?.data ?? [];

  const distinct = (key: SortKey) => [...new Set(all.map(SORT_VALUE[key]))].sort((a, b) => a.localeCompare(b));
  const phoneDigits = phoneFilter.replace(/\D/g, '');
  const rows = all
    .filter((p) => !customerFilter || p.frt_customer_code === customerFilter)
    .filter((p) => !platformFilter || SORT_VALUE.platform(p) === platformFilter)
    .filter((p) => !testerFilter || SORT_VALUE.tester(p) === testerFilter)
    .filter((p) => !phoneDigits || (p.phone_display ?? '').replace(/\D/g, '').includes(phoneDigits))
    .sort((a, b) => {
      const primary = SORT_VALUE[sort.key](a).localeCompare(SORT_VALUE[sort.key](b), undefined, {
        numeric: true,
      });
      return primary !== 0 ? primary * sort.dir : a.display.localeCompare(b.display);
    });

  const toggleSort = (key: SortKey) =>
    setSort((s) => (s.key === key ? { key, dir: s.dir === 1 ? -1 : 1 } : { key, dir: 1 }));
  const toggleExpand = (id: number, kind: 'tester' | 'phone') =>
    setExpanded((e) => (e && e.id === id && e.kind === kind ? null : { id, kind }));
  const filtersOn = customerFilter || platformFilter || testerFilter || phoneFilter;
  const columnCount = 8;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <h1 className="text-2xl font-semibold text-slate-900">Online Profiles</h1>
        {canEdit && <Button onClick={() => setCreating(true)}>+ New profile</Button>}
      </div>
      <div className="flex flex-wrap gap-2">
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Customer, tester, label or email"
          className="max-w-xs"
        />
        <select
          value={platformId}
          onChange={(e) => setPlatformId(Number(e.target.value))}
          className="rounded border border-slate-300 bg-white px-2 py-2 text-sm"
        >
          <option value={0}>All platform types</option>
          {(platforms?.data ?? []).map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
        <select
          value={active}
          onChange={(e) => setActive(e.target.value as 'true' | 'false' | 'all')}
          className="rounded border border-slate-300 bg-white px-2 py-2 text-sm"
        >
          <option value="true">Active</option>
          <option value="false">Inactive</option>
          <option value="all">All</option>
        </select>
        {filtersOn && (
          <Button
            variant="ghost"
            onClick={() => {
              setCustomerFilter('');
              setPlatformFilter('');
              setTesterFilter('');
              setPhoneFilter('');
            }}
          >
            Clear column filters
          </Button>
        )}
      </div>
      <Card>
        <CardBody className="overflow-x-auto p-0">
          {isLoading ? (
            <p className="p-4 text-sm text-slate-500">Loading…</p>
          ) : error ? (
            <p className="p-4 text-sm text-red-600">{(error as Error).message}</p>
          ) : all.length === 0 ? (
            <p className="p-4 text-sm text-slate-500">No online profiles.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-slate-500">
                <tr>
                  <SortTh label="Customer" k="customer" sort={sort} onSort={toggleSort} />
                  <SortTh label="Platform" k="platform" sort={sort} onSort={toggleSort} />
                  <Th>Profile</Th>
                  <SortTh label="Tester" k="tester" sort={sort} onSort={toggleSort} />
                  <SortTh label="Phone" k="phone" sort={sort} onSort={toggleSort} />
                  <Th>Email</Th>
                  <Th>OTP channels</Th>
                  <Th>Accounts</Th>
                </tr>
                <tr className="bg-white">
                  <FilterTh>
                    <FilterSelect value={customerFilter} onChange={setCustomerFilter} options={distinct('customer')} />
                  </FilterTh>
                  <FilterTh>
                    <FilterSelect value={platformFilter} onChange={setPlatformFilter} options={distinct('platform')} />
                  </FilterTh>
                  <FilterTh />
                  <FilterTh>
                    <FilterSelect value={testerFilter} onChange={setTesterFilter} options={distinct('tester')} />
                  </FilterTh>
                  <FilterTh>
                    <input
                      value={phoneFilter}
                      onChange={(e) => setPhoneFilter(e.target.value)}
                      placeholder="Search…"
                      aria-label="Filter by phone"
                      className="w-full rounded border border-slate-300 px-2 py-1 text-xs font-normal"
                    />
                  </FilterTh>
                  <FilterTh />
                  <FilterTh />
                  <FilterTh />
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={columnCount} className="px-3 py-4 text-slate-500">
                      No profiles match the filters.
                    </td>
                  </tr>
                )}
                {rows.map((p) => {
                  const open = expanded?.id === p.id ? expanded.kind : null;
                  return (
                    <Fragment key={p.id}>
                      <tr className={`border-t border-slate-100 ${p.active ? '' : 'text-slate-400'}`}>
                        <Td>
                          <Link
                            to="/customers/$id"
                            params={{ id: String(p.frt_customer_id) }}
                            className="text-brand-700 hover:underline"
                            title={p.frt_customer_name}
                          >
                            {p.frt_customer_code}
                          </Link>
                        </Td>
                        <Td>{platformLabel({ name: p.platform_name, type: p.platform_type_name })}</Td>
                        <Td>
                          <Link to="/profiles/$id" params={{ id: String(p.id) }} className="text-brand-700 hover:underline">
                            {p.display}
                          </Link>
                          {!p.active && ' (inactive)'}
                        </Td>
                        <Td>
                          {p.tester_id ? (
                            <ExpandButton open={open === 'tester'} onClick={() => toggleExpand(p.id, 'tester')}>
                              {p.tester_display}
                            </ExpandButton>
                          ) : (
                            'Shared'
                          )}
                        </Td>
                        <Td mono>
                          {p.phone_id ? (
                            <ExpandButton open={open === 'phone'} onClick={() => toggleExpand(p.id, 'phone')}>
                              {p.phone_display}
                            </ExpandButton>
                          ) : (
                            <Missing />
                          )}
                        </Td>
                        <Td>{p.email ?? <Missing />}</Td>
                        <Td>{p.oob_summary ? p.oob_summary.split('; ').length : <Missing />}</Td>
                        <Td>{p.account_count}</Td>
                      </tr>
                      {open && (
                        <tr className="bg-slate-50">
                          <td colSpan={columnCount} className="px-3 py-2">
                            {open === 'tester' ? (
                              <AccountsPanel
                                title={`Accounts of ${p.tester_display}`}
                                query={{ tester_id: p.tester_id! }}
                              />
                            ) : (
                              <AccountsPanel
                                title={`Accounts on ${p.phone_display} (profile phone or OTP)`}
                                query={{ phone_number_id: p.phone_id! }}
                              />
                            )}
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          )}
        </CardBody>
      </Card>
      {all.length > 0 && (
        <p className="text-xs text-slate-500">
          Showing {rows.length} of {all.length} profiles. Click a tester or phone to see its accounts.
        </p>
      )}
      <ProfileFormDialog open={creating} onClose={() => setCreating(false)} onSaved={() => undefined} />
    </div>
  );
}

/** Accounts for an expanded tester / phone row. */
function AccountsPanel({
  title,
  query,
}: {
  title: string;
  query: { tester_id?: number; phone_number_id?: number };
}) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['accounts', { ...query, per_page: 200 }],
    queryFn: () => accounts.list({ ...query, per_page: 200 }),
  });
  const rows = data?.data ?? [];
  return (
    <div className="space-y-1">
      <div className="text-xs font-semibold text-slate-700">{title}</div>
      {isLoading ? (
        <p className="text-xs text-slate-500">Loading…</p>
      ) : error ? (
        <p className="text-xs text-red-600">{(error as Error).message}</p>
      ) : rows.length === 0 ? (
        <p className="text-xs text-slate-500">No accounts.</p>
      ) : (
        <table className="w-full text-xs">
          <thead className="text-left text-slate-500">
            <tr>
              <th className="py-1 pr-3 font-medium">Account</th>
              <th className="py-1 pr-3 font-medium">Online profile</th>
              <th className="py-1 pr-3 font-medium">Status</th>
              <th className="py-1 pr-3 font-medium">Tiller</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((a) => (
              <tr key={a.id} className="border-t border-slate-200">
                <td className="py-1 pr-3">
                  <Link
                    to="/accounts/$id"
                    params={{ id: String(a.id) }}
                    className="font-mono text-brand-700 hover:underline"
                  >
                    {a.display_name}
                  </Link>
                </td>
                <td className="py-1 pr-3">
                  {a.customer_profile_id ? (
                    <Link
                      to="/profiles/$id"
                      params={{ id: String(a.customer_profile_id) }}
                      className="text-brand-700 hover:underline"
                    >
                      {a.customer_profile_display}
                    </Link>
                  ) : (
                    '—'
                  )}
                </td>
                <td className="py-1 pr-3">{a.account_status}</td>
                <td className="py-1 pr-3">{a.tiller_status}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {data && data.total > rows.length && (
        <p className="text-xs text-slate-500">
          Showing the first {rows.length} of {data.total}.
        </p>
      )}
    </div>
  );
}

function ExpandButton({
  open,
  onClick,
  children,
}: {
  open: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-expanded={open}
      className="inline-flex items-center gap-1 text-brand-700 hover:underline"
    >
      <span className="text-[10px] text-slate-400">{open ? '▼' : '▶'}</span>
      {children}
    </button>
  );
}

function SortTh({
  label,
  k,
  sort,
  onSort,
}: {
  label: string;
  k: SortKey;
  sort: { key: SortKey; dir: 1 | -1 };
  onSort: (k: SortKey) => void;
}) {
  const active = sort.key === k;
  return (
    <th className="px-3 py-2 font-medium" aria-sort={active ? (sort.dir === 1 ? 'ascending' : 'descending') : 'none'}>
      <button type="button" onClick={() => onSort(k)} className="inline-flex items-center gap-1 hover:text-slate-800">
        {label}
        <span className="text-[10px]">{active ? (sort.dir === 1 ? '▲' : '▼') : '↕'}</span>
      </button>
    </th>
  );
}

function FilterTh({ children }: { children?: React.ReactNode }) {
  return <th className="px-3 pb-2 font-normal">{children}</th>;
}

function FilterSelect({
  value,
  onChange,
  options,
}: {
  value: string;
  onChange: (v: string) => void;
  options: string[];
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full rounded border border-slate-300 bg-white px-1 py-1 text-xs"
    >
      <option value="">All</option>
      {options.map((o) => (
        <option key={o} value={o}>
          {o}
        </option>
      ))}
    </select>
  );
}

export function ProfileDetailPage() {
  const { id: idStr } = useParams({ strict: false }) as { id?: string };
  const id = Number(idStr);
  const canEdit = useCanEdit();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [toggling, setToggling] = useState<OobEntry | null>(null);
  const { data: p, isLoading, error } = useQuery({
    queryKey: ['customer-profile', id],
    queryFn: () => customerProfiles.get(id),
    enabled: Number.isFinite(id),
  });
  const toggleActive = useMutation({
    mutationFn: (active: boolean) => customerProfiles.update(id, { active }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['customer-profile'] });
      void queryClient.invalidateQueries({ queryKey: ['customer-profiles'] });
    },
  });

  if (isLoading) return <p className="text-sm text-slate-500">Loading…</p>;
  if (error) return <p className="text-sm text-red-600">{(error as Error).message}</p>;
  if (!p) return <p className="text-sm text-slate-500">Not found</p>;
  const incomplete = !p.phone_id || !p.email || p.oob_entries.length === 0;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <Link to="/profiles" className="text-sm text-brand-700 hover:underline">
            ← Online Profiles
          </Link>
          <h1 className="text-2xl font-semibold text-slate-900">
            {p.display}
            {!p.active && <span className="ml-2 text-base text-slate-500">(inactive)</span>}
          </h1>
        </div>
        {canEdit && (
          <div className="flex gap-2">
            <Button onClick={() => setEditing(true)}>Edit</Button>
            <Button
              variant="secondary"
              disabled={toggleActive.isPending}
              onClick={() => toggleActive.mutate(!p.active)}
            >
              {p.active ? 'Deactivate' : 'Reactivate'}
            </Button>
          </div>
        )}
      </div>
      {incomplete && (
        <p className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          Phone, email or OTP channels are missing (this profile was created from earlier account
          data). Use Edit to complete it.
        </p>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Profile</CardTitle>
        </CardHeader>
        <CardBody>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-y-1 gap-x-4 text-sm">
            <Item label="FRT Customer">
              <Link
                to="/customers/$id"
                params={{ id: String(p.frt_customer_id) }}
                className="text-brand-700 hover:underline"
              >
                {p.frt_customer_code} ({p.frt_customer_name})
              </Link>
            </Item>
            <Item label="Platform">
              {platformLabel({ name: p.platform_name, type: p.platform_type_name })} ·{' '}
              {modeLabel(p.profile_mode)}
            </Item>
            <Item label="Tester">
              {p.tester_id ? (
                <Link to="/testers/$id" params={{ id: String(p.tester_id) }} className="text-brand-700 hover:underline">
                  {p.tester_display}
                </Link>
              ) : (
                'Shared'
              )}
            </Item>
            <Item label="Label">{p.label ?? '—'}</Item>
            <Item label="Phone" mono>
              {p.phone_id ? (
                <Link to="/assets/phones/$id" params={{ id: String(p.phone_id) }} className="text-brand-700 hover:underline">
                  {p.phone_display}
                </Link>
              ) : (
                '—'
              )}
            </Item>
            <Item label="Email">{p.email ?? '—'}</Item>
            <Item label="Address">{formatAddress(p) || '—'}</Item>
            <Item label="Notes">{p.notes ?? '—'}</Item>
          </dl>
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Account Access (OTP channels)</CardTitle>
        </CardHeader>
        <CardBody className="overflow-x-auto">
          <OobTable entries={p.oob_entries} onToggle={canEdit ? setToggling : undefined} />
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Accounts ({p.accounts.length})</CardTitle>
        </CardHeader>
        <CardBody>
          {p.accounts.length === 0 ? (
            <p className="text-sm text-slate-500">No accounts roll up to this profile yet.</p>
          ) : (
            <ul className="text-sm space-y-1">
              {p.accounts.map((a) => (
                <li key={a.id}>
                  <Link to="/accounts/$id" params={{ id: String(a.id) }} className="font-mono text-brand-700 hover:underline">
                    {a.display_name}
                  </Link>
                  {a.account_status !== 'active' && <span className="text-slate-500"> ({a.account_status})</span>}
                </li>
              ))}
            </ul>
          )}
        </CardBody>
      </Card>

      <OobStatusLogCard profileId={id} />
      <HistoryCard queryKey={['customer-profile', id, 'history']} load={() => customerProfiles.history(id)} />
      <OobStatusDialog profileId={id} entry={toggling} onClose={() => setToggling(null)} />
      <ProfileFormDialog open={editing} onClose={() => setEditing(false)} profile={p} onSaved={() => undefined} />
    </div>
  );
}

/** OTP channels table, shared with the account pages. With onToggle (the
 *  profile page, admin/senior) each row gets a Turn On/Off action. */
export function OobTable({
  entries,
  onToggle,
}: {
  entries: OobEntry[];
  onToggle?: (e: OobEntry) => void;
}) {
  if (entries.length === 0) {
    return <p className="text-sm text-amber-700">No OTP channels recorded yet.</p>;
  }
  return (
    <table className="w-full text-sm">
      <thead className="text-left text-slate-500">
        <tr>
          <th className="py-1 pr-3">OOB Type</th>
          <th className="py-1 pr-3">Value</th>
          <th className="py-1 pr-3">Device</th>
          <th className="py-1 pr-3">Ownership</th>
          <th className="py-1 pr-3">Current Owner</th>
          <th className="py-1 pr-3">Status</th>
          {onToggle && <th className="py-1 pr-3" />}
        </tr>
      </thead>
      <tbody>
        {entries.map((e) => (
          <tr key={e.id} className={`border-t border-slate-100 ${e.status === 'off' ? 'text-slate-400' : ''}`}>
            <td className="py-1 pr-3">{e.oob_type_name}</td>
            <td className="py-1 pr-3">
              {e.phone_number_id ? (
                <Link
                  to="/assets/phones/$id"
                  params={{ id: String(e.phone_number_id) }}
                  className="font-mono text-brand-700 hover:underline"
                >
                  {e.phone_display}
                </Link>
              ) : (
                e.contact_value ?? '—'
              )}
            </td>
            <td className="py-1 pr-3">
              {e.device_id ? (
                <Link to="/assets/devices/$id" params={{ id: String(e.device_id) }} className="text-brand-700 hover:underline">
                  {e.device_display}
                </Link>
              ) : (
                <span className="text-slate-500">No device</span>
              )}
            </td>
            <td className="py-1 pr-3">{e.device_ownership_name ?? '—'}</td>
            <td className="py-1 pr-3">
              {/* Phone kinds: the number's owner; otherwise the device's. */}
              {(e.phone_number_id ? e.phone_owner_display : e.device_owner_display) ?? '—'}
            </td>
            <td className="py-1 pr-3">
              <StatusBadge status={e.status} />
            </td>
            {onToggle && (
              <td className="py-1 pr-3 text-right">
                <Button type="button" size="sm" variant="secondary" onClick={() => onToggle(e)}>
                  Turn {e.status === 'on' ? 'Off' : 'On'}
                </Button>
              </td>
            )}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function StatusBadge({ status }: { status: OobStatus | 'removed' }) {
  const style =
    status === 'on'
      ? 'bg-green-100 text-green-800'
      : status === 'off'
        ? 'bg-slate-200 text-slate-700'
        : 'bg-red-100 text-red-800';
  const label = status === 'on' ? 'On' : status === 'off' ? 'Off' : 'Removed';
  return <span className={`rounded px-1.5 py-0.5 text-xs font-medium ${style}`}>{label}</span>;
}

/** Turn a channel On/Off with an optional note (logged). */
function OobStatusDialog({
  profileId,
  entry,
  onClose,
}: {
  profileId: number;
  entry: OobEntry | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [note, setNote] = useState('');
  const next: OobStatus = entry?.status === 'on' ? 'off' : 'on';
  const save = useMutation({
    mutationFn: () => customerProfiles.setOobStatus(profileId, entry!.id, next, note.trim() || null),
    onSuccess: () => {
      setNote('');
      for (const key of ['customer-profile', 'customer-profiles', 'account', 'accounts']) {
        void queryClient.invalidateQueries({ queryKey: [key] });
      }
      onClose();
    },
  });
  return (
    <Dialog
      open={entry !== null}
      onClose={onClose}
      title={entry ? `Turn ${next === 'on' ? 'On' : 'Off'}: ${entry.oob_type_name}` : ''}
      footer={
        <>
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="button" disabled={save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? 'Saving…' : `Turn ${next === 'on' ? 'On' : 'Off'}`}
          </Button>
        </>
      }
    >
      <label className="block text-sm">
        <span className="block text-slate-600">Note (optional)</span>
        <textarea
          value={note}
          maxLength={1000}
          rows={3}
          onChange={(e) => setNote(e.target.value)}
          className="block w-full rounded border border-slate-300 px-2 py-1.5 text-sm"
          placeholder="Why the change, e.g. SIM suspended, authenticator re-enrolled"
        />
      </label>
      {save.error && <p className="mt-2 text-sm text-red-600">{(save.error as Error).message}</p>}
    </Dialog>
  );
}

/** The profile's OTP status log, newest first. */
function OobStatusLogCard({ profileId }: { profileId: number }) {
  const { data, isLoading } = useQuery({
    queryKey: ['customer-profile', profileId, 'oob-status-log'],
    queryFn: () => customerProfiles.oobStatusLog(profileId),
  });
  const rows = data?.data ?? [];
  return (
    <Card>
      <CardHeader>
        <CardTitle>OTP status log</CardTitle>
      </CardHeader>
      <CardBody className="overflow-x-auto">
        {isLoading ? (
          <p className="text-sm text-slate-500">Loading…</p>
        ) : rows.length === 0 ? (
          <p className="text-sm text-slate-500">No status changes yet.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-left text-slate-500">
              <tr>
                <Th>When</Th>
                <Th>Changed by</Th>
                <Th>Channel</Th>
                <Th>Status</Th>
                <Th>Note</Th>
              </tr>
            </thead>
            <tbody>
              {rows.map((e) => (
                <tr key={e.id} className="border-t border-slate-100 align-top">
                  <Td>{new Date(e.at).toLocaleString()}</Td>
                  <Td>{e.actor_username ?? <span className="text-slate-500">system</span>}</Td>
                  <Td>{e.channel}</Td>
                  <Td>
                    {e.prior_status === null ? (
                      <span>
                        Added · <StatusBadge status={e.new_status} />
                      </span>
                    ) : (
                      <span>
                        <StatusBadge status={e.prior_status} /> → <StatusBadge status={e.new_status} />
                      </span>
                    )}
                  </Td>
                  <Td>{e.note ?? ''}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </CardBody>
    </Card>
  );
}

function Missing() {
  return <span className="text-amber-600">missing</span>;
}

function Th({ children }: { children: React.ReactNode }) {
  return <th className="px-3 py-2 font-medium">{children}</th>;
}

function Td({ children, mono }: { children: React.ReactNode; mono?: boolean }) {
  return <td className={`px-3 py-2 ${mono ? 'font-mono' : ''}`}>{children}</td>;
}

function Item({ label, mono, children }: { label: string; mono?: boolean; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-2 gap-x-4">
      <dt className="text-slate-500">{label}</dt>
      <dd className={mono ? 'font-mono' : undefined}>{children}</dd>
    </div>
  );
}
